/**
 * CmtMock — development stand-in for the real Classification Management Tool popup.
 *
 * Renders as a Radix Dialog containing a radio group of the three valid
 * classification options. Used by CmtWrapper when the real CMT JS is not present.
 * Never rendered in production.
 */

import * as Dialog from '@radix-ui/react-dialog'
import { useState } from 'react'
import { CMT_OPTIONS, matchCmtOption } from './types'
import type { CmtResult } from './types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The current value, used to pre-select the matching option. */
  value: CmtResult | undefined
  onSelect: (result: CmtResult) => void
}

export function CmtMock({ open, onOpenChange, value, onSelect }: Props) {
  const [selected, setSelected] = useState<CmtResult>(
    () => matchCmtOption(value).result
  )

  // Re-sync if value changes while dialog is closed
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSelected(matchCmtOption(value).result)
    }
    onOpenChange(next)
  }

  const handleOk = () => {
    onSelect(selected)
    onOpenChange(false)
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
          aria-describedby="cmt-mock-desc"
        >
          <div className="mb-1 flex items-center gap-2">
            {/* DEV badge to make it obvious this is the mock */}
            <span className="rounded bg-amber-9 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
              DEV
            </span>
            <Dialog.Title className="text-sm font-semibold text-fg">
              Classification Tool
            </Dialog.Title>
          </div>
          <Dialog.Description id="cmt-mock-desc" className="sr-only">
            Classification marking selection.
          </Dialog.Description>

          {/* Radio group */}
          <fieldset className="space-y-2 border-0 p-0">
            <legend className="sr-only">Classification options</legend>
            {CMT_OPTIONS.map((opt) => {
              const id = `cmt-opt-${opt.label.replace(/\W+/g, '-')}`
              const isSelected =
                [...selected.disseminationControls].sort().join(',') ===
                [...opt.result.disseminationControls].sort().join(',')

              return (
                <label
                  key={opt.label}
                  htmlFor={id}
                  className={[
                    'flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-[12px] font-mono transition-colors',
                    isSelected
                      ? 'border-blue-8 bg-blue-2 text-fg'
                      : 'border-line bg-surface text-fg hover:bg-fill',
                  ].join(' ')}
                >
                  <input
                    id={id}
                    type="radio"
                    name="cmt-classification"
                    className="accent-blue-9"
                    checked={isSelected}
                    onChange={() => setSelected(opt.result)}
                  />
                  {opt.label}
                </label>
              )
            })}
          </fieldset>

          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleOk}
              className="rounded bg-blue-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            >
              OK
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
